import { ArrowDownRight, ArrowUpRight, ReceiptText, ShieldAlert } from 'lucide-react'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import type { ReactNode } from 'react'
import { Card, MoneyValue } from '@shared/design-system/ui'
import type { MonetaryValue } from '@shared/lib/money/money'
import {
  columns,
  header,
  headerAmounts,
  headerCopy,
  label,
  list,
  meta,
  panel,
  row,
  rowAmounts,
  sectionTitle,
  toneIcon,
} from './financial-summary.css'

interface FinancialSourceView {
  gross: MonetaryValue
  net: MonetaryValue
  charges: number
}
interface FinancialLossView {
  count: number
  amount: MonetaryValue
  ratePct: number
}
export interface FinancialSummaryView {
  sources: {
    oneTime: FinancialSourceView
    subscriptionInitial: FinancialSourceView
    renewals: FinancialSourceView
    subscriptions: FinancialSourceView
    total: FinancialSourceView
  }
  losses: {
    fxLosses?: FinancialLossView
    refunds: FinancialLossView
    preventedRefunds: FinancialLossView
    chargebacks: FinancialLossView
    chargebackFees: { count: number; generated: MonetaryValue; paid: MonetaryValue }
    total: MonetaryValue
  }
  result: MonetaryValue
}

export function FinancialSummaryPanel({ data }: { data: FinancialSummaryView }) {
  const metricsEnabled = useFeatureFlag('chargebackDashboardMetrics')
  return (
    <Card padding="md" className={panel} data-testid="financial-summary-panel">
      <div className={header}>
        <div className={headerCopy}>
          <span className={sectionTitle}>Resultado do período</span>
          <span className={meta}>
            Entradas líquidas sem reserva, menos estornos, prevenções, chargebacks e multas pagas.
          </span>
        </div>
        <div className={headerAmounts}>
          <MoneyValue amount={data.result.amount} currency={data.result.currency} size="lg" />
          <span className={meta}>
            Entradas{' '}
            <MoneyValue
              amount={data.sources.total.net.amount}
              currency={data.sources.total.net.currency}
              size="sm"
            />{' '}
            · Perdas{' '}
            <MoneyValue
              amount={data.losses.total.amount}
              currency={data.losses.total.currency}
              size="sm"
            />
          </span>
        </div>
      </div>
      <div className={columns}>
        <section>
          <div className={sectionTitle}>
            <span className={toneIcon({ tone: 'positive' })}>
              <ArrowUpRight size={15} />
            </span>
            De onde veio o dinheiro
          </div>
          <p className={meta}>
            Uma compra pode incluir avulsas e assinaturas. Os valores se somam; a mesma cobrança
            pode aparecer nas duas categorias.
          </p>
          <div className={list}>
            <SourceRow
              labelText="Vendas avulsas"
              source={data.sources.oneTime}
              caption={`${data.sources.oneTime.charges} cobranças`}
            />
            <SourceRow
              labelText="Assinaturas"
              source={data.sources.subscriptions}
              caption={`${data.sources.subscriptionInitial.charges} primeiras · ${data.sources.renewals.charges} renovações`}
            />
          </div>
        </section>
        <section>
          <div className={sectionTitle}>
            <span className={toneIcon({ tone: 'negative' })}>
              <ArrowDownRight size={15} />
            </span>
            O que reduziu o resultado
          </div>
          <div className={list}>
            {data.losses.fxLosses ? (
              <LossRow
                icon={<ReceiptText size={14} />}
                labelText="Perdas cambiais"
                value={data.losses.fxLosses}
              />
            ) : null}
            <LossRow
              icon={<ReceiptText size={14} />}
              labelText="Estornos"
              value={data.losses.refunds}
            />
            <LossRow
              icon={<ShieldAlert size={14} />}
              labelText="Prevenções de chargeback"
              value={data.losses.preventedRefunds}
            />
            <LossRow
              icon={<ShieldAlert size={14} />}
              labelText="Chargebacks"
              value={data.losses.chargebacks}
            />
            <div className={row}>
              <div>
                <span className={label}>Multas de chargeback pagas</span>
                {metricsEnabled ? (
                  <span className={meta} data-testid="chargeback-fees-generated">
                    {data.losses.chargebackFees.count} multas geradas no período, total de{' '}
                    <MoneyValue
                      amount={data.losses.chargebackFees.generated.amount}
                      currency={data.losses.chargebackFees.generated.currency}
                      size="sm"
                    />
                  </span>
                ) : (
                  <span className={meta}>{data.losses.chargebackFees.count} multas geradas</span>
                )}
              </div>
              <MoneyValue
                amount={data.losses.chargebackFees.paid.amount}
                currency={data.losses.chargebackFees.paid.currency}
                size="sm"
              />
            </div>
          </div>
        </section>
      </div>
    </Card>
  )
}

function SourceRow({
  labelText,
  source,
  caption,
}: {
  labelText: string
  source: FinancialSourceView
  caption: string
}) {
  return (
    <div className={row}>
      <div>
        <span className={label}>{labelText}</span>
        <span className={meta}>{caption}</span>
      </div>
      <div className={rowAmounts}>
        <MoneyValue amount={source.gross.amount} currency={source.gross.currency} size="sm" />
        <span className={meta}>
          líquido <MoneyValue amount={source.net.amount} currency={source.net.currency} size="sm" />
        </span>
      </div>
    </div>
  )
}

function LossRow({
  icon,
  labelText,
  value,
}: {
  icon: ReactNode
  labelText: string
  value: FinancialLossView
}) {
  return (
    <div className={row}>
      <div>
        <span className={label}>
          {icon} {labelText}
        </span>
        <span className={meta}>
          {value.count} ocorrências ·{' '}
          {value.ratePct.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}% das cobranças
        </span>
      </div>
      <MoneyValue amount={value.amount.amount} currency={value.amount.currency} size="sm" />
    </div>
  )
}
